import { DISABLED_INERT, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

/**
 * The « … » menu of a card: the gestures that are real but rare, folded behind
 * one control (refonte admin maquette, BUILD-852).
 *
 * ## Why it exists at all
 *
 * A card of the experience catalogue offered six buttons - Annoncer, Retirer,
 * Monter, Descendre, Dupliquer, Modifier - all of them the same weight, so the
 * two an operator comes for were as loud as the four they meet twice a cycle.
 * The maquette keeps three on the face of the card and folds the rest here.
 * Nothing is REMOVED: a gesture that is hidden rather than folded is a gesture
 * one has to already know exists, and the confirmations of BUILD-751 stay in
 * front of every one of them.
 *
 * ## Hand-written rather than pulled from Radix
 *
 * This repository has no Radix runtime today - `components/ui/` holds three
 * hand-written files - so a dropdown primitive would be a new dependency in a
 * wave whose whole point is that it changes no route, no schema and no
 * behaviour. What a menu needs is small and it is all here: a name on the
 * trigger, `aria-haspopup`, Escape, an outside click, and focus handed back to
 * the trigger when it closes.
 *
 * ## The outside click listens on `mousedown`, deliberately
 *
 * On `click` the very event that OPENS the menu would bubble to the document
 * and shut it again in the same tick. `mousedown` fires before the trigger's
 * click, and the handler ignores anything inside this component's own subtree,
 * so the two never fight. It also means a menu opened by `fireEvent.click` in a
 * unit test stays open, which is what a test of the gesture behind it needs.
 */

export interface ActionMenuItem {
  /** Stable key AND what a test targets. Never the label: labels are French. */
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  /**
   * A glyph BEFORE the label, for an entry that also exists as an icon
   * elsewhere on the screen.
   *
   * Optional, and the items that pass none render exactly as they did before
   * it existed. The case it was added for is « Dupliquer » (§4.2, issues #117
   * and #116): the same gesture is offered twice on a step - once as the
   * gutter's copy icon, once here - and the two are only legible as ONE gesture
   * if they carry the same glyph. The « … » entry of a versioned card wears it
   * too, for the same reason: it is the gesture every gutter draws as that
   * icon. It is the caller's job to make it `aria-hidden`: the label beside it
   * is the name.
   */
  readonly icon?: ReactNode;
}

export interface ActionMenuProps {
  /** The accessible name of the trigger. The glyph says nothing on its own. */
  label: string;
  items: readonly ActionMenuItem[];
  /**
   * Non-interactive lines under the items - where the technical identity of a
   * card goes (BUILD-852). Rendered inside the panel but OUTSIDE the `menu`
   * role: a block of text is not a `menuitem`, and announcing it as one would
   * promise a gesture that does not exist.
   */
  footer?: ReactNode;
  /**
   * `dots` is the « … » of a card, whose whole job is to take no room.
   * `named` writes the label on the trigger, for a menu that IS the offer -
   * « Dupliquer une expérience existante » in the head of a page, where three
   * dots would be a gesture nobody would find.
   */
  trigger?: 'dots' | 'named';
  /** Classes for the named trigger only - the dots one is fixed by design. */
  triggerClassName?: string;
  /**
   * The trigger node, handed to a caller that has to PLACE something against it.
   *
   * A menu whose item opens a second, floating surface - the duplication choice
   * of §4.2 - needs the exact element that surface stands under and hands the
   * focus back to. Without it the caller anchors on a wrapper `div`, which
   * measures the same but cannot take focus, so Escape would drop the keyboard
   * on `<body>`. Optional, and nobody else passes it.
   */
  triggerRef?: (node: HTMLButtonElement | null) => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function ActionMenu({
  label,
  items,
  footer,
  trigger: triggerKind = 'dots',
  triggerClassName,
  triggerRef,
  disabled = false,
  className,
  'data-testid': testId,
}: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const menuId = useId();
  /** True as soon as ONE item carries a glyph - see the item's className below. */
  const glyphs = items.some((item) => item.icon !== undefined);

  /**
   * The trigger node, held here and handed to a caller that has to place
   * something against it.
   *
   * `useCallback` and not an inline arrow: a ref whose identity changes every
   * render is detached and re-attached every render, so an inline one would
   * call `triggerRef(null)` then `triggerRef(node)` on each pass - two state
   * writes per menu, on every card of a grid, at every keystroke of the filter
   * above it.
   */
  const holdTrigger = useCallback(
    (node: HTMLButtonElement | null) => {
      trigger.current = node;
      triggerRef?.(node);
    },
    [triggerRef],
  );

  /** Shut it and hand the focus back: a menu that closes into nowhere is lost. */
  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close(true);
      // Tab LEAVES the menu, which is what `role="menu"` promises: a menu is
      // one stop of the page's tab order, not a pocket of several.
      if (event.key === 'Tab') close(false);
    }
    function onDown(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Node && container.current?.contains(target)) return;
      close(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  /**
   * The arrow keys `role="menu"` promises.
   *
   * Claiming the role without them is the one combination that MISLEADS: a
   * screen reader announces a menu, so its user reaches for Up and Down, and
   * the items are plain buttons that only answer Tab. Either the role goes or
   * the keys arrive - and the role is the honest description of what these
   * items are, so the keys arrive.
   *
   * Focus is moved on the live DOM rather than through a roving `tabIndex`
   * state: the panel is unmounted when closed, so there is no order to
   * remember between openings, and a state would be a second copy of where the
   * focus already is.
   */
  function moveFocus(event: ReactKeyboardEvent<HTMLDivElement>) {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const enabled = [...(panel.current?.querySelectorAll('[role="menuitem"]') ?? [])].filter(
      (node): node is HTMLButtonElement => node instanceof HTMLButtonElement && !node.disabled,
    );
    if (enabled.length === 0) return;
    event.preventDefault();
    const here = enabled.findIndex((node) => node === document.activeElement);
    const last = enabled.length - 1;
    // Wrapping, because a menu is a ring: Down on the last item is how one
    // gets back to the first without counting.
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? last
          : event.key === 'ArrowDown'
            ? here >= last
              ? 0
              : here + 1
            : here <= 0
              ? last
              : here - 1;
    enabled[next]?.focus();
  }

  /**
   * The first item takes the focus when the menu opens.
   *
   * A menu that opens behind the focus is a menu a keyboard user has to Tab
   * into, which is not what `aria-haspopup` told them would happen.
   */
  useEffect(() => {
    if (!open) return;
    const first = panel.current?.querySelector('[role="menuitem"]:not([disabled])');
    if (first instanceof HTMLElement) first.focus();
  }, [open]);

  return (
    <div ref={container} className={cn('relative', className)}>
      <button
        ref={holdTrigger}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        data-testid={testId}
        onClick={() => setOpen((was) => !was)}
        className={
          triggerKind === 'named'
            ? cn(buttonVariants({ variant: 'outline' }), triggerClassName)
            : `inline-flex h-9 cursor-pointer items-center justify-center rounded-md border-2 border-transparent px-3 font-semibold text-bleu-nuit text-sm leading-none transition-colors duration-150 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none ${DISABLED_INERT}`
        }
      >
        {/* Three dots as a glyph and not an ellipsis character: the accessible
            name is already on the button, and a screen reader reading « … »
            aloud would add nothing to it. */}
        {triggerKind === 'named' ? label : <span aria-hidden="true">•••</span>}
      </button>
      {open ? (
        <div
          id={menuId}
          ref={panel}
          onKeyDown={moveFocus}
          className="absolute right-0 z-20 mt-1 min-w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          <div role="menu" aria-label={label}>
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                disabled={item.disabled ?? false}
                onClick={() => {
                  // The focus goes back to the trigger BEFORE the gesture
                  // fires: without it the panel unmounts under the focused
                  // item and the keyboard lands on `<body>`. A gesture that
                  // opens a dialog takes it from there, which is correct - the
                  // trigger is only ever the resting place.
                  close(true);
                  item.onSelect();
                }}
                className={`${
                  glyphs ? 'flex items-center gap-2' : 'block'
                } w-full cursor-pointer px-3 py-2 text-left text-gray-800 text-sm hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:cursor-not-allowed ${DISABLED_INERT}`}
              >
                {/*
                  The column is reserved for EVERY item as soon as ONE carries a
                  glyph, empty where there is none: indenting only the item that
                  has an icon would leave a four-item menu with a ragged left
                  edge, which reads as a defect rather than as an emphasis.
                */}
                {glyphs
                  ? (item.icon ?? <span aria-hidden="true" className="size-4 shrink-0" />)
                  : null}
                {item.label}
              </button>
            ))}
          </div>
          {footer === undefined ? null : (
            <div className="mt-1 border-gray-200 border-t px-3 pt-2 pb-1">{footer}</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
